import React from 'react';

import Course, { Props as CourseProps } from '../Course';

import Section from 'Elements/Section';
import Box from 'Elements/Box';

import Grid, { Col } from 'Components/Grid';
import SupportBlock, { SupportBlockProps } from 'Components/SupportBlock';
import Video, { VideoProps } from 'Components/Video';
import Social, { SocialSupportBlockProps } from 'Components/SupportBlock/Social';
import Card, { CardProps } from 'Components/Card';
import LineList, { LineListProps } from 'Components/List/LineList';
import InformationBlock from 'Components/InformationBlock';

import Information, { InformationBannerProps } from 'Modules/Banner/Information';
import Bookings, { BookingCardsProps } from 'Modules/Cards/Booking';
import BannerLink, { LinkBannerProps } from 'Modules/Banner/Link';

export interface Props extends CourseProps {
    supportBlock?: SupportBlockProps;
    questionsBlock?: SupportBlockProps;
    information?: InformationBannerProps;
    keyPoint?: SupportBlockProps;
    social?: SocialSupportBlockProps;
    bookings?: BookingCardsProps;
    card?: (CardProps & { id: string })[];
    bannerLink?: LinkBannerProps;
    lineList?: LineListProps;
    courseOverview: {
        text?: string | React.ReactNode;
        video?: VideoProps;
    }[];
    whatYoullStudy: {
        heading: string;
        text: string | React.ReactNode;
    };
    bookingQueryText?: string | React.ReactNode;
    feeText: string | React.ReactNode;
}

const CoachingCourse: React.FC<Props> = props => {
    const {
        breadcrumbs,
        heroBanner,
        supportBlock,
        information,
        keyPoint,
        social,
        bookings,
        card,
        bannerLink,
        lineList,
        courseOverview,
        whatYoullStudy,
        bookingQueryText,
        questionsBlock,
        feeText
    } = props;

    return (
        <Course breadcrumbs={breadcrumbs} heroBanner={heroBanner}>
            <Grid>
                <Col m={9} l={7}>
                    <Section id="overview">
                        <h2>About this course</h2>
                        {courseOverview.map((item, index) => (
                            <React.Fragment key={index}>
                                {item.text && item.text}
                                {item.video && <Video {...item.video} />}
                            </React.Fragment>
                        ))}
                    </Section>
                </Col>
                {supportBlock && (
                    <Col m={10} l={[9, 13]}>
                        <Section id="support-block">
                            <SupportBlock {...supportBlock} />
                        </Section>
                    </Col>
                )}
            </Grid>
            {information && (
                <Section id="information">
                    <Grid>
                        <Col m={10} l={13}>
                            <Information {...information} />
                        </Col>
                    </Grid>
                </Section>
            )}
            <Section id="what-study">
                <Grid>
                    <Col>
                        <h2>{whatYoullStudy.heading}</h2>
                    </Col>
                    <Col m={9} l={8}>
                        {whatYoullStudy.text}
                    </Col>
                    {keyPoint && keyPoint.heading && (
                        <Col m={10} l={4}>
                            <aside>
                                <SupportBlock {...keyPoint} />
                            </aside>
                        </Col>
                    )}
                </Grid>
            </Section>
            <Section id="fees">
                <Grid>
                    {lineList && lineList.items.length >= 1 && (
                        <>
                            <Col>
                                <h2>Fees and finance</h2>
                            </Col>
                            <Col m={10} l={8}>
                                {feeText && <p>{feeText}</p>}
                                <InformationBlock
                                    spacing={{
                                        base: { padding: '1rem 1.5rem', margin: '0 0 1rem' },
                                        large: { margin: '0' }
                                    }}
                                >
                                    <LineList {...lineList} />
                                </InformationBlock>
                            </Col>
                        </>
                    )}
                    <Col m={10} l={4}>
                        <aside>
                            <Box p={{ bottom: 3 }}>
                                {questionsBlock?.children && <SupportBlock {...questionsBlock} />}
                            </Box>
                            {social && <Social {...social} />}
                        </aside>
                    </Col>
                </Grid>
            </Section>
            <Section id="bookings">
                <Grid>
                    <Col m={10} l={13}>
                        {bookings && bookingQueryText && <h2>Ready to book</h2>}
                        {bookings && bookings.items.length >= 1 && <Bookings {...bookings} />}
                    </Col>
                    {bookingQueryText && (
                        <Col m={9} l={7}>
                            {bookingQueryText}
                        </Col>
                    )}
                </Grid>
            </Section>
            {card && (
                <Section id="related-courses">
                    <Grid>
                        <Col m={10} l={13}>
                            <h2>You may be interested in these courses</h2>
                        </Col>
                    </Grid>
                    <Grid>
                        {card.map(item => (
                            <Col m={5} l={4} key={item.id}>
                                <Card {...item} />
                            </Col>
                        ))}
                    </Grid>
                </Section>
            )}
            {bannerLink && (
                <Section id="banner">
                    <BannerLink {...bannerLink} />
                </Section>
            )}
        </Course>
    );
};

export default CoachingCourse;
